import { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Typography, Alert, AlertTitle, CircularProgress, Chip, Stack, Paper,
  Grid, Divider, Tooltip, Link, Table, TableBody, TableCell, TableHead, TableRow,
} from '@mui/material';
import QueryStatsIcon from '@mui/icons-material/QueryStats';

// 전사 사용량 — 데이터: GET /api/usage/portfolio
//
// 🔴 이 화면의 가장 중요한 문장은 맨 위 경고다.
//   요청 수는 사용량이 아니다. 8월 실측으로 전 존 요청 6,697만 중 실제 브라우저
//   페이지 조회는 64,970 — 1,031:1 이다. api.raymedical.com(EMS 장비 연동) 하나가
//   raymedical 존의 96% 를 차지한다. 요청 수를 그대로 실으면
//   "8월에 6,700만 사용" 이라는 완전히 틀린 문장이 된다.
//
// 🔴 미귀속 호스트가 이 화면의 존재 이유 절반이다.
//   제품 대시보드는 제품에 붙은 것만 보여준다. 그러면 매핑 안 된 호스트는 어느
//   화면에도 안 나온다 — 합계에서 조용히 빠진다. 여기에 표로 세우면 그 목록이
//   곧 제품 SSOT 에 채워야 할 작업 목록이 된다.

const n = (v) => (v ?? 0).toLocaleString('ko-KR');
const TYPE_LABEL = { web: '웹', api: 'API', infra: '기반', internal: '사내', env: '개발·테스트' };

function Stat({ value, label, hint, color }) {
  const body = (
    <Paper variant="outlined" sx={{ p: 1.5, height: '100%' }}>
      <Typography sx={{
        fontSize: 24, fontWeight: 750, fontFamily: 'monospace', lineHeight: 1.15,
        color: color || 'text.primary',
      }}>
        {value}
      </Typography>
      <Typography sx={{
        fontSize: 12, color: 'text.disabled',
        borderBottom: hint ? '1px dotted' : 'none', display: 'inline-block', cursor: hint ? 'help' : 'default',
      }}>
        {label}
      </Typography>
    </Paper>
  );
  return hint ? <Tooltip title={hint}>{body}</Tooltip> : body;
}

export default function UsagePage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    fetch('/api/usage/portfolio')
      .then(async (r) => {
        const j = await r.json().catch(() => ({}));
        if (!r.ok) throw Object.assign(new Error(j.error || `HTTP ${r.status}`), { hint: j.hint });
        return j;
      })
      .then((j) => { if (alive) { setData(j); setLoading(false); } })
      .catch((e) => { if (alive) { setErr(e); setLoading(false); } });
    return () => { alive = false; };
  }, []);

  // 사내 도구(이름 있음)와 진짜 미분류(이름 없음)를 갈라 본다 —
  // 앞의 것은 "알고 있으나 제품이 아님", 뒤의 것은 "아직 모름" 이다. 성격이 다르다.
  const { internal, unknown, unknownTotal } = useMemo(() => {
    const list = data?.unattributed || [];
    const internal = list.filter((u) => u.label);
    const unknown = list.filter((u) => !u.label);
    return { internal, unknown, unknownTotal: unknown.reduce((a, u) => a + u.requests, 0) };
  }, [data]);

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }
  if (err) {
    return (
      <Alert severity="warning" sx={{ my: 3 }}>
        <AlertTitle>{err.message}</AlertTitle>
        {err.hint}
      </Alert>
    );
  }
  if (data?.schemaOk === false) {
    return (
      <Alert severity="error" sx={{ my: 3 }}>
        <AlertTitle>사용량 계약 버전이 다릅니다</AlertTitle>
        받은 스키마: <code>{data.schema || '(없음)'}</code> — 화면이 기대하는 것은{' '}
        <code>ray-usage-snapshot/v1</code> 입니다. 잘못된 숫자를 그리지 않기 위해 표시를 멈춥니다.
      </Alert>
    );
  }

  const products = data.products || [];
  const zones = data.zones || [];
  const cov = data.coverage || {};
  const partial = cov.days_observed < cov.days_in_period;

  const liveProducts = products.reduce((a, p) => a + p.live_requests, 0);
  const liveOthers = (data.unattributed || []).reduce((a, u) => a + u.requests, 0);
  const liveAll = liveProducts + liveOthers;
  const humanPV = zones.reduce((a, z) => a + (z.human_page_views || 0), 0);
  const zoneReq = zones.reduce((a, z) => a + (z.requests || 0), 0);

  return (
    <Box sx={{ pb: 8 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
        <QueryStatsIcon color="primary" />
        <Typography variant="h5" sx={{ fontWeight: 800, letterSpacing: '-0.02em' }}>
          전사 사용량
        </Typography>
        {data.period?.value && (
          <Chip size="small" label={data.period.value} sx={{ height: 21, fontWeight: 700 }} />
        )}
      </Stack>
      <Typography sx={{ color: 'text.secondary', mb: 2.5, maxWidth: 720 }}>
        Cloudflare 존의 호스트별 요청을 매일 적재해 제품 SSOT 로 귀속시킨 값입니다.
        {partial && (
          <> 이 달 <b>{cov.days_in_period}일 중 {cov.days_observed}일</b>만 관측됐습니다 — 한 달치가 아닙니다.</>
        )}
      </Typography>

      {/* 🔴 이 화면에서 가장 중요한 문장 */}
      <Alert severity="error" variant="outlined" icon={false} sx={{ mb: 3 }}>
        <AlertTitle sx={{ fontWeight: 750 }}>요청 수를 방문자 수로 읽지 마십시오</AlertTitle>
        <Typography sx={{ fontSize: 14, lineHeight: 1.7 }}>
          아래 숫자는 <b>봇·기계 호출을 포함한 요청 수</b>입니다. 장비 연동 API 한 곳이 존 전체의
          대부분을 차지하는 일이 흔해서, 요청 수를 사용량으로 읽으면 정확히 거꾸로 해석됩니다.
          {humanPV > 0 && zoneReq > 0 && (
            <> 이 기간 존 합계 요청 <b>{n(zoneReq)}</b> 중 실제 브라우저 페이지 조회는{' '}
              <b>{n(humanPV)}</b> 입니다.</>
          )}
        </Typography>
        <Typography sx={{ fontSize: 14, lineHeight: 1.7, mt: 1 }}>
          사람이 본 페이지 수는 <b>존 단위로만</b> 알 수 있습니다 — 호스트별 데이터에는 봇/사람
          구분이 없어 제품 단위로 쪼갤 근거가 없습니다. 없는 것을 만들지 않습니다.
        </Typography>
      </Alert>

      <Grid container spacing={1} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <Stat value={n(liveProducts)} label="제품 요청 (봇 포함)" hint="제품 SSOT 에 매핑된 호스트의 요청 합계" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Stat value={products.length} label="관측된 제품" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Stat value={n(unknownTotal)} label="미분류 요청"
            color={unknownTotal > 0 ? 'warning.main' : undefined}
            hint="어느 제품인지 확정하지 못한 호스트 — 합계에서 빼지 않고 그대로 보여줍니다" />
        </Grid>
        <Grid item xs={6} sm={3}>
          <Stat value={humanPV > 0 ? n(humanPV) : '—'} label="사람 페이지 조회 (존 합계)"
            hint={humanPV > 0 ? '존 합계 데이터셋의 브라우저 분포 기준' : 'CF 존 합계를 아직 수집하지 못했습니다'} />
        </Grid>
      </Grid>

      {/* 제품별 */}
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.13em', textTransform: 'uppercase', color: 'text.disabled', mb: 1 }}>
        제품별 · {products.length}개
      </Typography>
      <Paper variant="outlined" sx={{ mb: 4, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>제품</TableCell>
              <TableCell align="right">운영 요청 (봇 포함)</TableCell>
              <TableCell align="right">비중</TableCell>
              <TableCell align="right">개발·테스트</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {products.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>
                  <Link component={RouterLink} to={`/products/${p.id}`} sx={{ fontWeight: 600 }}>{p.name}</Link>
                  <Typography sx={{ fontSize: 11, color: 'text.disabled', fontFamily: 'monospace' }}>
                    {(p.hosts || []).map((h) => h.host).join(' · ')}
                  </Typography>
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{n(p.live_requests)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>
                  {liveAll ? `${((p.live_requests / liveAll) * 100).toFixed(1)}%` : '—'}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.disabled' }}>
                  {p.env_requests ? n(p.env_requests) : '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {/* 🔴 미분류 — 이 목록이 곧 SSOT 에 채워야 할 작업 목록이다 */}
      <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.13em', textTransform: 'uppercase', color: 'text.disabled', mb: 1 }}>
        미분류 · {unknown.length}건
      </Typography>
      <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1, maxWidth: 720 }}>
        어느 제품인지 확정하지 못한 호스트입니다. <b>합계에서 빼지 않습니다</b> — 빼면 어디로 갔는지
        알 수 없게 됩니다. 이 목록이 곧 제품 SSOT 의 <code>hosts:</code> 에 채워야 할 작업 목록입니다.
      </Typography>
      <Paper variant="outlined" sx={{ mb: 4, overflowX: 'auto' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>호스트</TableCell>
              <TableCell>존</TableCell>
              <TableCell align="right">요청</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {unknown.map((u) => (
              <TableRow key={u.host} hover>
                <TableCell sx={{ fontFamily: 'monospace' }}>
                  {u.host}
                  {u.note === 'api_only' && (
                    <Tooltip title="프런트엔드가 없다고 확인된 호스트입니다 — 방문 지표로 읽으면 안 됩니다">
                      <Chip size="small" variant="outlined" label="프런트엔드 없음"
                        sx={{ ml: 1, height: 17, fontSize: 10 }} />
                    </Tooltip>
                  )}
                </TableCell>
                <TableCell sx={{ color: 'text.disabled', fontSize: 12 }}>{u.zone}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{n(u.requests)}</TableCell>
              </TableRow>
            ))}
            {unknown.length === 0 && (
              <TableRow><TableCell colSpan={3} sx={{ color: 'text.disabled' }}>미분류 호스트가 없습니다.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Paper>

      {/* 사내 도구 — 제품은 아니지만 무엇인지 안다 */}
      {internal.length > 0 && (
        <>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.13em', textTransform: 'uppercase', color: 'text.disabled', mb: 1 }}>
            사내 도구 · {internal.length}건
          </Typography>
          <Paper variant="outlined" sx={{ mb: 4, overflowX: 'auto' }}>
            <Table size="small">
              <TableBody>
                {internal.map((u) => (
                  <TableRow key={u.host} hover>
                    <TableCell sx={{ fontWeight: 600 }}>{u.label}
                      <Chip size="small" variant="outlined" label={TYPE_LABEL[u.type] || u.type}
                        sx={{ ml: 1, height: 17, fontSize: 10 }} />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: 12, color: 'text.disabled' }}>{u.host}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{n(u.requests)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      {/* 존 합계 — 사람 PV 는 여기서만 나온다 */}
      {zones.length > 0 && (
        <>
          <Typography sx={{ fontSize: 12, fontWeight: 700, letterSpacing: '.13em', textTransform: 'uppercase', color: 'text.disabled', mb: 1 }}>
            존 합계 · {zones.length}개
          </Typography>
          <Paper variant="outlined" sx={{ mb: 3, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>존</TableCell>
                  <TableCell align="right">요청 (봇 포함)</TableCell>
                  <TableCell align="right">페이지 조회</TableCell>
                  <TableCell align="right">사람 조회</TableCell>
                  <TableCell align="right">순 방문</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {zones.map((z) => (
                  <TableRow key={z.zone} hover>
                    <TableCell sx={{ fontFamily: 'monospace' }}>{z.zone}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace' }}>{n(z.requests)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{n(z.page_views)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', fontWeight: 700 }}>{n(z.human_page_views)}</TableCell>
                    <TableCell align="right" sx={{ fontFamily: 'monospace', color: 'text.secondary' }}>{n(z.uniques)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </>
      )}

      <Divider sx={{ my: 3 }} />
      <Typography sx={{ fontSize: 12, color: 'text.disabled', lineHeight: 1.8 }}>
        호스트별 상세 데이터셋은 <b>보존이 {data.limits?.host_retention_days ?? 7}일</b>입니다 — 매일 적재하지
        않으면 그 달의 제품별 사용량은 되돌려 만들 수 없습니다.<br />
        Worker 에서 나가는 <b>외부 아웃바운드 호스트는 제외</b>했습니다 — 우리 서비스 사용량이 아닙니다.<br />
        출처 <code>{data.sourceRepo}</code> · <code>{data.sourcePath}</code> · 기준{' '}
        {data.updated_at ? String(data.updated_at).slice(0, 16).replace('T', ' ') : '—'}
      </Typography>
    </Box>
  );
}
